import {Component, EventEmitter, Input, OnInit, Output, ViewChild} from '@angular/core';
import { GridComponent, GridDataResult } from '@progress/kendo-angular-grid';
import { NavHelperService } from '../../../../services/nav/nav-helper.service';
import { AppDataSyncService } from '../../../../services/app-data-sync/app-data-sync.service';
import { PtLuxonDate } from '../../../../models/projects/date.models';
import { LockedDayService } from '../../../../services/locked-day/locked-day.service';
import { process, State } from '@progress/kendo-data-query';
import { JackingActivityService } from '../../../../services/jacking-activity/jacking-activity.service';
import { DateParserService } from '../../../../services/daily-log/daily-log-time/date-parser-service';
import { columns } from './jacking-activity-list-component.config';
import { fieldPositioningColumns } from './field-positioning-list-component.config';
import { JackingActivityEditor } from '../../../../models/projects/jacking-activity.models';
import { NavController } from 'ionic-angular';
import { JackingActivityEditPage } from '../jacking-activity-edit/jacking-activity-edit';
import { DailyLogService } from '../../../../services/daily-log/daily-log.service';
import { ValidationResult } from '../../../../models/projects/validation.models';
import { ValidationService } from '../../../../services/validation/validation.service';
import { PermissionsService } from '../../../../services/permissions/permissions.service';
import { JackingPermissions } from '../../../../models/permissions/permission.models';
import { FieldPositioningEditor } from '../../../../models/field-positioning/field-positioning.models';
import { FieldPositioningEditPage } from '../../../field-positioning/field-positioning-edit/field-positioning-edit';
import { FieldPositioningService } from '../../../../services/field-positioning/field-positioning.service';
import { OnSiteMovementService } from '../../../../services/on-site-movement/on-site-movement.service';
import { OnSiteMovementEditor } from '../../../../models/on-site-movement/on-site-movement.models';
import { onSiteMovementColumns } from './on-site-movement-list-component.config';
import { OnSiteMovementEditPage } from '../../../on-site-movement/on-site-movement-edit/on-site-movement-edit';
import { DateService } from '../../../../services/date/date.service';
import { JackingActivityReportService } from '../../../../services/jacking-activity/jacking-activity-report.service';
import { VesselService } from '../../../../services/vessel/vessel.service';
import { HttpResponse } from '@angular/common/http';
import { TimeZoneService } from '../../../../services/time-zone/time-zone.service';
import { DailyLogDatePickerComponent } from '../../../shared/components/daily-log-date-picker/daily-log-date-picker';
import { ImplementerService } from '../../../../services/implementer/implementer.service';
import { cloneDeep } from 'lodash';
import { CompleteDayFilters } from '../../../../models/complete-day.models';
import { CompleteDayService } from '../../../../services/complete-day/complete-day.service';
import { AppConfig } from '../../../../app/app.config';

@Component({
  selector: 'app-jacking-activity-list-v1',
  templateUrl: './jacking-activity-list-component.component.html'
})
export class JackingActivityListComponent implements OnInit {
  @ViewChild('jackingActivityGrid') grid?: GridComponent;
  @ViewChild('fieldPositioningGrid') fieldPositioningGrid?: GridComponent;
  @ViewChild('onSiteMovementGrid') onSiteMovementGrid?: GridComponent;

  @Input() externalDate?: PtLuxonDate;
  @Input() validationResult: ValidationResult = new ValidationResult();
  @Input() validationLoading = false;
  @Input() dateLoading = false;

  @Output() uiChange = new EventEmitter<{
    validationResult?: ValidationResult;
    validationLoading?: boolean;
    loading?: boolean;
    firstLoadComplete?: boolean;
  }>();

  config = AppConfig.settings;
  loading: boolean = false;

  // Grid variables
  jackingActivityData: JackingActivityEditor[];
  state: State;
  gridData: GridDataResult;
  columns = columns;

  // Field Positioning grid variables
  fieldPositioningGridData: GridDataResult;
  fieldPositioningData: FieldPositioningEditor[];
  fieldPositioningState: State;
  fieldPositioningColumns = fieldPositioningColumns;

  // On-Site Movement grid variables
  onSiteMovementGridData: GridDataResult;
  onSiteMovementData: OnSiteMovementEditor[];
  onSiteMovementState: State;
  onSiteMovementColumns = onSiteMovementColumns;

  initialLoad: boolean;
  firstLoadComplete: boolean = false;
  dateBeforeInitialSetup: boolean;
  sidebarCollapsed: boolean;
  dailyLogDate: PtLuxonDate = new PtLuxonDate();
  currentVesselId: number;
  permissions: JackingPermissions = new JackingPermissions();

  jackingGridLoading: boolean;
  fieldPositioningGridLoading: boolean;
  onSiteMovementGridLoading: boolean;

  dayCompleted: boolean;
  isImplementerActiveForDay: boolean;

  constructor(
    private navCtrl: NavController,
    private appDataSyncService: AppDataSyncService,
    public dailyLogService: DailyLogService,
    private lockedDayService: LockedDayService,
    private jackingActivityService: JackingActivityService,
    public dateParserService: DateParserService,
    private validationService: ValidationService,
    private navHelperService: NavHelperService,
    private fieldPositioningService: FieldPositioningService,
    private permissionService: PermissionsService,
    private onSiteMovementSerivce: OnSiteMovementService,
    private dateService: DateService,
    private jackingActivityReportService: JackingActivityReportService,
    private vesselService: VesselService,
    private timeZoneService: TimeZoneService,
    private implementerService: ImplementerService,
    private completeDayService: CompleteDayService,
  ) {
  }

  async ngOnInit() {
    this.loading = true;
    this.initialLoad = true;
    this.navHelperService.currentSidebarCollapsed.subscribe(sidebarCollapsed => {
      this.sidebarCollapsed = sidebarCollapsed;
    });

    this.vesselService.getEditor().then(async editor => {
      this.currentVesselId = editor.id;
    });

    this.permissions = await this.permissionService.jackingPermissions();
  }

  async loadForDate(date: PtLuxonDate, refresh: boolean = true) {
    this.externalDate = date;

    // init states (fix from earlier)
    this.state = this.jackingActivityService.getState();
    this.fieldPositioningState = this.fieldPositioningService.getState();
    this.onSiteMovementState = this.onSiteMovementSerivce.getState();

    await this.loadData(refresh);
  }

  setUiState(state: {
    loading: boolean;
    initialLoad: boolean;
    firstLoadComplete: boolean;
    validationLoading: boolean;
    validationResult: ValidationResult;
    dateBeforeInitialSetup: boolean;
    dayCompleted: boolean;
  }) {
    // router now owns these; keep local values in sync
    this.loading = state.loading;
    this.initialLoad = state.initialLoad;
    this.firstLoadComplete = state.firstLoadComplete;
    this.validationLoading = state.validationLoading;
    this.validationResult = state.validationResult;
    this.dateBeforeInitialSetup = state.dateBeforeInitialSetup;
    this.dayCompleted = state.dayCompleted;
  }

  async loadData(refresh: boolean) {
    this.uiChange.emit({
      loading: true,
      firstLoadComplete: false,
      validationLoading: this.validationLoading,
      validationResult: this.validationResult,
    });

    this.loading = true;
    this.jackingGridLoading = true;
    this.fieldPositioningGridLoading = true;
    this.onSiteMovementGridLoading = true;
    this.firstLoadComplete = false;

    // Use passed date if supplied, otherwise fallback to service
    this.dailyLogDate = this.externalDate || await this.dailyLogService.getCurrentLogDay();

    await this.checkImplementerActive();
    await this.checkDayCompleted();
    const endOfDay = await this.dateService.getEndOfDayLocally(this.dailyLogDate.dateTime);

    if (refresh) {
      await this.appDataSyncService.refreshJackingDataForDate(this.dailyLogDate);

      this.validationResult = new ValidationResult();
      Promise.all(
        [
          this.jackingActivityService.getActivityDataForDay(this.dailyLogDate, endOfDay),
          this.fieldPositioningService.getActivitiesForDateFromLocal(this.dailyLogDate, endOfDay),
          this.onSiteMovementSerivce.getOnSiteMovementRecordsForDay(this.dailyLogDate, endOfDay)
        ]).then(values => {
        this.jackingActivityData = values[0];
        this.fieldPositioningData = values[1];
        this.onSiteMovementData = values[2];

        this.updateGrid();
        this.loading = false;
        this.uiChange.emit({
          loading: false,
          firstLoadComplete: true,
        });
      });

      await this.validateJackingPage();
    }

    this.initialLoad = false;
    this.firstLoadComplete = true;
  }

  async ionViewEnter() {
    this.state = this.jackingActivityService.getState();
    this.fieldPositioningState = this.fieldPositioningService.getState();
    this.onSiteMovementState = this.onSiteMovementSerivce.getState();
    this.loadData(true);
  }

  async dayUnlockedForEditing() {
    await this.checkDayCompleted();
  }

  loadingStarted() {
    this.firstLoadComplete = false;
    this.uiChange.emit({ firstLoadComplete: false });
  }

  async checkImplementerActive() {
    this.isImplementerActiveForDay = await this.implementerService.implementerIsActive(this.config.isVesselEnvironment, cloneDeep(this.dailyLogDate));
  }

  public async editJackingActivity(editor: JackingActivityEditor) {
    if (editor) {
      this.navCtrl.push(JackingActivityEditPage, {
        activity: editor,
        currentDate: this.dailyLogDate
      }, {animation: 'ios-transition'});
    }
  }

  updateGrid() {
    // guard against undefined state if something calls loadData before init
    if (!this.state) {
      this.state = this.jackingActivityService.getState();
    }
    if (!this.fieldPositioningState) {
      this.fieldPositioningState = this.fieldPositioningService.getState();
    }
    if (!this.onSiteMovementState) {
      this.onSiteMovementState = this.onSiteMovementSerivce.getState();
    }

    this.gridData = process(this.jackingActivityData || [], this.state);
    this.jackingGridLoading = false;

    this.fieldPositioningGridData = process(this.fieldPositioningData || [], this.fieldPositioningState);
    this.fieldPositioningGridLoading = false;

    this.onSiteMovementGridData = process(this.onSiteMovementData || [], this.onSiteMovementState);
    this.onSiteMovementGridLoading = false;
  }

  downloadJackingArrival(row: JackingActivityEditor, incrementVersion: boolean) {
    this.setRowLoadingIcon(row, incrementVersion);

    const downloadLink = incrementVersion
      ? this.jackingActivityReportService
        .generateJackingArrivalDownloadLink(this.dailyLogDate, this.currentVesselId, row.id, this.config.isImplementerEnvironment)
      : this.jackingActivityReportService
        .downloadJackingArrivalDownloadLink(this.dailyLogDate, this.currentVesselId, row.id, this.config.isImplementerEnvironment);

    this.jackingActivityReportService
      .generateFormBlob(downloadLink)
      .subscribe(
        (resp: HttpResponse<Blob>) => {
          const contentDisposition = resp.headers.get('content-disposition');
          const file = resp.body;
          const fileURL = URL.createObjectURL(file);
          const searchTerm = 'filename=';
          let filename = contentDisposition.slice(contentDisposition.indexOf(searchTerm) + searchTerm.length);

          // fix bug where for some reason the filename sometimes has an extra set of quotation makes around it, this results in chrome
          // replacing these with underscores in the downloaded file resulting in a download name like _filename.pdf_
          if (filename.slice(0, 1) === '"') {
            filename = filename.substring(1, filename.length - 1);
          }

          const link = document.createElement('a');
          link.href = fileURL;
          link.download = filename;
          link.click();
          row.viewFormLoading = false;
          row.generateFormLoading = false;
        });
  }

  downloadJackingDeparture(row: JackingActivityEditor, incrementVersion: boolean) {
    this.setRowLoadingIcon(row, incrementVersion);

    const downloadLink = incrementVersion
      ? this.jackingActivityReportService
        .generateJackingDepartureDownloadLink(this.dailyLogDate, this.currentVesselId, row.id, this.config.isImplementerEnvironment)
      : this.jackingActivityReportService
        .downloadJackingDepartureDownloadLink(this.dailyLogDate, this.currentVesselId, row.id, this.config.isImplementerEnvironment);

    this.jackingActivityReportService
      .generateFormBlob(downloadLink)
      .subscribe(
        (resp: HttpResponse<Blob>) => {
          const contentDisposition = resp.headers.get('content-disposition');
          const file = resp.body;
          const fileURL = URL.createObjectURL(file);
          const searchTerm = 'filename=';
          let filename = contentDisposition.slice(contentDisposition.indexOf(searchTerm) + searchTerm.length);

          // fix bug where for some reason the filename sometimes has an extra set of quotation makes around it, this results in chrome
          // replacing these with underscores in the downloaded file resulting in a download name like _filename.pdf_
          if (filename.slice(0, 1) === '"') {
            filename = filename.substring(1, filename.length - 1);
          }

          const link = document.createElement('a');
          link.href = fileURL;
          link.download = filename;
          link.click();
          row.viewFormLoading = false;
          row.generateFormLoading = false;
        });
  }


  public async editFieldPositioning(editor: FieldPositioningEditor) {
    if (editor) {
      this.navCtrl.push(FieldPositioningEditPage, {
        activity: editor,
        dailyLogDate: this.dailyLogDate
      }, {animation: 'ios-transition'});
    }
  }

  downloadFieldPositioning(row: FieldPositioningEditor, isPortrait: boolean) {
    if (isPortrait) {
      row.portraitLoading = true;
    } else {
      row.landscapeLoading = true;
    }

    const downloadLink = this.fieldPositioningService.generateDprDownloadLink(this.dailyLogDate, row.id, isPortrait, this.config.isImplementerEnvironment);


    this.jackingActivityReportService
      .generateFormBlob(downloadLink)
      .subscribe(
        (resp: HttpResponse<Blob>) => {
          const contentDisposition = resp.headers.get('content-disposition');
          const file = resp.body;
          const fileURL = URL.createObjectURL(file);
          const searchTerm = 'filename=';
          let filename = contentDisposition.slice(contentDisposition.indexOf(searchTerm) + searchTerm.length);

          // fix bug where for some reason the filename sometimes has an extra set of quotation makes around it, this results in chrome
          // replacing these with underscores in the downloaded file resulting in a download name like _filename.pdf_
          if (filename.slice(0, 1) === '"') {
            filename = filename.substring(1, filename.length - 1);
          }

          const link = document.createElement('a');
          link.href = fileURL;
          link.download = filename;
          link.click();

          if (isPortrait) {
            row.portraitLoading = false;
          } else {
            row.landscapeLoading = false;
          }
        });
  }

  public async editOnSiteMovementRecord(editor: OnSiteMovementEditor, readOnly: boolean) {
    if (editor) {
      await this.navCtrl.push(OnSiteMovementEditPage, {
        onSiteMovementRecord: editor,
        dailyLogDate: this.dailyLogDate,
        readOnly: readOnly
      }, {animation: 'ios-transition'});
    }
  }

  async validateJackingPage() {
    this.validationLoading = true;
    this.uiChange.emit({ validationLoading: true });

    this.validationService.validateFieldPositioning(this.dailyLogDate, this.validationResult).then(fieldPositioningResult => {
      this.validationService.validateJackingActivities(this.dailyLogDate, fieldPositioningResult, null).then(jackingResult => {
        this.validationService.validateOnSiteMovement(this.dailyLogDate, jackingResult, null).then(result => {
          this.validationResult = result;
          this.validationLoading = false;

          this.uiChange.emit({
            validationLoading: this.validationLoading,
            validationResult: this.validationResult,
          });
        });
      });
    });
  }

  async checkDayCompleted() {
    if (!this.dailyLogDate.dateTime) {
      return;
    }

    const filters = new CompleteDayFilters();
    filters.date = this.dailyLogDate.dateTime;

    this.dayCompleted = await this.completeDayService.checkDay(filters);
  }

  dateBeforeInitialSetupChanged(value: boolean) {
    this.dateBeforeInitialSetup = value;
  }

  dateLoadingStarted() {
    this.firstLoadComplete = false;
  }

  private setRowLoadingIcon(row: JackingActivityEditor, incrementVersion: boolean) {
    if (incrementVersion) {
      row.generateFormLoading = true;
    } else {
      row.viewFormLoading = true;
    }
  }
}



// WEBPACK FOOTER //
// ./src/pages/jacking-activities/v1/jacking-activity-list-component/jacking-activity-list-component.component.ts